import React, { useRef, useState } from 'react';
import { Popover } from '@material-ui/core';
import { important, px } from 'csx';
import { AddToAssortmentItem, PlanItem } from 'src/pages/AssortmentBuild/Planning.types';
import { IconLink } from 'src/components/Headerbar/Links/IconLink';
import styles from 'src/components/Headerbar/Headerbar.styles';
import { getIconBadgeColor, parseCartPlanItems, parsePlanItems } from 'src/components/PlanQueue/PlanQueue.utils';
import { PlanPopoverContent } from './PlanLinkPopoverContent';

const PlanningLinkContainerStyle: React.CSSProperties = {
  maxWidth: important(px(145)),
};

const LinkContainerStyle: React.CSSProperties = {
  maxWidth: important(px(115)),
};
const iconStyle: React.CSSProperties = {
  color: '#00a89b',
};
const refreshIconStyle: React.CSSProperties = {
  transform: 'scaleX(-1)',
  color: '#00a89b',
};
const iconStatusContainer: React.CSSProperties = {
  position: 'relative',
  left: 8,
};

interface PlanLinkProps {
  planType: 'assortment' | 'allocation';
  showAllocation: boolean;
  askForRefresh: boolean;
  assortmentItems: PlanItem[];
  allocationItems: PlanItem[];
  cartItems: AddToAssortmentItem[];
  onClick: () => void;
  onRefresh: () => void;
  assortmentPlanError?: boolean;
  allocationPlanError?: boolean;
  addToAsstError?: boolean;
}

/** checks for data in the correct items array based on the active context */
function getStatusBooleanFromContext(
  planType: 'assortment' | 'allocation',
  assortmentItems: PlanItem[],
  allocationItems: PlanItem[],
) {
  return planType === 'assortment' ? assortmentItems.length > 0 : allocationItems.length > 0;
}

export const PlanLink = ({
  askForRefresh,
  assortmentItems,
  allocationItems,
  cartItems,
  planType,
  showAllocation,
  onClick,
  onRefresh,
  assortmentPlanError,
  allocationPlanError,
  addToAsstError,
}: PlanLinkProps) => {
  const anchorElement = useRef(null);
  const [isOpen, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(false);
    onClick();
  };
  const handleMouseOver = () => setOpen(true);
  const handleMouseLeave = () => setOpen(false);

  const parsedAssortmentItems = parsePlanItems(assortmentItems);
  const parsedAllocationItems = parsePlanItems(allocationItems);
  const parsedCartItems = parseCartPlanItems(cartItems);

  const planInProgress = getStatusBooleanFromContext(
    planType,
    parsedAssortmentItems.processing,
    showAllocation ? parsedAllocationItems.processing : [],
  );
  const showBadge = getStatusBooleanFromContext(
    planType,
    parsedAssortmentItems.pending,
    showAllocation ? parsedAllocationItems.pending : [],
  );
  // cart is currently not affected by context
  const addToAsstInProgress = parsedCartItems.processing.length > 0;

  // show error (red) badge for plan items failed and addToAsst failed
  const errorBadge =
    getStatusBooleanFromContext(
      planType,
      parsedAssortmentItems.failed,
      showAllocation ? parsedAllocationItems.failed : [],
    ) || parsedCartItems.failed.length > 0;

  // pulse badge for desired status types, this feature is off until requested
  const pulseBadge = false;

  // need to get the correct data based on active context's planType for determining badge color
  const itemsOfActiveContext =
    planType === 'assortment'
      ? {
          addProcessing: parsedCartItems.processing,
          addFailed: parsedCartItems.failed,
          planPending: parsedAssortmentItems.pending,
          planProcessing: parsedAssortmentItems.processing,
          planCompleted: parsedAssortmentItems.completed,
          planFailed: parsedAssortmentItems.failed,
        }
      : {
          addProcessing: parsedCartItems.processing,
          addFailed: parsedCartItems.failed,
          planPending: parsedAllocationItems.pending,
          planProcessing: parsedAllocationItems.processing,
          planCompleted: parsedAllocationItems.completed,
          planFailed: parsedAllocationItems.failed,
        };
  const badgeColor = getIconBadgeColor(itemsOfActiveContext);

  const isProcessing = planInProgress || addToAsstInProgress;

  return (
    <React.Fragment>
      <span style={iconStatusContainer}>
        {askForRefresh && !isProcessing ? (
          <IconLink
            ref={anchorElement}
            id="PlanRefreshReady"
            iconClass="fa fa-undo"
            linkClass="clickable refreshable"
            containerStyle={LinkContainerStyle}
            text=""
            iconStyle={refreshIconStyle}
            onClick={onRefresh}
            tooltipText="Refresh your view to get the latest plan data"
          />
        ) : (
          <IconLink
            ref={anchorElement}
            id="PlanStatus"
            iconClass={
              assortmentPlanError || allocationPlanError
                ? 'fa fa-exclamation-circle text-warning'
                : !(planInProgress || addToAsstInProgress)
                  ? 'fa fa-check-circle'
                  : 'fa fa-spinner fa-spin'
            }
            containerStyle={PlanningLinkContainerStyle}
            iconStyle={assortmentPlanError || allocationPlanError ? { color: '#f44336' } : iconStyle}
            text=""
            tooltipText={
              assortmentPlanError || allocationPlanError
                ? '⚠️ Cannot Fetch Plan Queue'
                : !(planInProgress || addToAsstInProgress)
                  ? 'Latest plan data is displayed'
                  : 'Plan items are being processed'
            }
          />
        )}
      </span>
      <IconLink
        id="PlanLink"
        iconClass={'fa fa-paper-plane'}
        containerStyle={PlanningLinkContainerStyle}
        text={'Plan Queue'}
        showBadge={showBadge}
        errorBadge={errorBadge}
        pulseBadge={pulseBadge}
        badgeColor={badgeColor}
        onClick={handleClick}
        onMouseOver={handleMouseOver}
        onMouseLeave={handleMouseLeave}
        ref={anchorElement}
      />
      <Popover
        open={isOpen}
        classes={{ root: styles.filterPopover }}
        anchorEl={anchorElement.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        container={anchorElement.current}
        PaperProps={{
          onMouseEnter: handleMouseOver,
          onMouseLeave: handleMouseLeave,
        }}
        disableRestoreFocus={true}
      >
        <PlanPopoverContent
          assortmentItems={parsedAssortmentItems}
          allocationItems={parsedAllocationItems}
          cartItems={parsedCartItems}
          showAllocation={showAllocation}
          assortmentPlanError={assortmentPlanError}
          allocationPlanError={allocationPlanError}
          addToAsstError={addToAsstError}
        />
      </Popover>
    </React.Fragment>
  );
};
